import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

interface AllocationData {
  name: string;
  value: number;
  color: string;
  description: string;
}

const mockAllocationData: AllocationData[] = [
  {
    name: 'Direct Aid',
    value: 35,
    color: '#8B4513',
    description: 'Emergency relief and humanitarian assistance'
  },
  {
    name: 'Education',
    value: 30,
    color: '#D2691E',
    description: 'Peace education and forgiveness training'
  },
  {
    name: 'Community Programs',
    value: 18,
    color: '#FFD700',
    description: 'Local chapter support and events'
  },
  {
    name: 'Research',
    value: 8,
    color: '#228B22',
    description: 'Impact studies and evidence building'
  },
  {
    name: 'Operations',
    value: 5,
    color: '#4a90a4',
    description: 'Administrative and platform costs'
  },
  {
    name: 'Founder Recognition',
    value: 4,
    color: '#9370DB',
    description: 'Supports initiative, leadership and full-time dedication to the movement'
  }
];

const FundingAllocation = () => {
  return (
    <div className="bg-card rounded-lg shadow-md p-6 border border-border">
      <div className="mb-6">
        <h2 className="text-2xl font-headline font-bold text-foreground mb-2">
          Transparent Fund Allocation
        </h2>
        <p className="text-sm font-body text-muted-foreground">
          Every dollar tracked and allocated for maximum humanitarian impact
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="h-96 flex items-center justify-center">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={mockAllocationData}
                cx="50%"
                cy="50%"
                labelLine={false}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {mockAllocationData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: number) => `${value}%`}
                contentStyle={{
                  backgroundColor: '#F5F5DC',
                  border: '1px solid rgba(139, 69, 19, 0.2)',
                  borderRadius: '8px'
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="space-y-4">
          {mockAllocationData.map((item, index) => (
            <div
              key={index}
              className="p-4 bg-muted rounded-lg hover:bg-primary/5 transition-colors duration-200"
            >
              <div className="flex items-start space-x-3">
                <div
                  className="w-4 h-4 rounded-full mt-1 flex-shrink-0"
                  style={{ backgroundColor: item.color }}
                />
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-base font-body font-semibold text-foreground">
                      {item.name}
                    </h3>
                    <span className="text-lg font-headline font-bold text-primary">
                      {item.value}%
                    </span>
                  </div>
                  <p className="text-sm font-body text-muted-foreground">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-8 p-4 bg-accent/10 rounded-lg border border-accent/20">
        <div className="flex items-start space-x-3">
          <div className="p-2 bg-accent/20 rounded-lg">
            <svg
              className="w-5 h-5 text-accent-foreground"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div>
            <h4 className="text-sm font-body font-semibold text-foreground mb-1">
              100% Transparency Guarantee
            </h4>
            <p className="text-xs font-body text-muted-foreground">
              All donations are tracked in real-time with quarterly impact reports sent to
              contributors. Third-party audits ensure accountability.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FundingAllocation;